import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Collapse, Paper, Stack, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { HttpAuth } from "../../http";
import { ControlledTextField } from "../../components/forms/ControlledTextField";
import { ControlledTextPasswordFieldFilled } from "../../components/forms/ControlledTextPasswordField";

import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { getCouponFieldStyle } from "../../components/Checkout/CouponStyles";
import { useTheme } from "../../util/themes";

import type { RecuperoContraseniaResponse } from "../../types/user/passwordRecoveryModels";

const schemaMail = yup.object({
    mail: yup.string().email("Email inválido").required("El email es obligatorio"),
});

const schemaCodigo = yup.object({
    codigo: yup
        .string()
        .required("El código es obligatorio")
        .matches(/^\d{6}$/, "Ingresá el código de 6 dígitos"),
});

const schemaContrasenia = yup.object({
    password: yup.string().required("La contraseña es obligatoria"),
    passwordConfirm: yup
        .string()
        .required("Debe repetir la contraseña")
        .oneOf([yup.ref("password")], "Las contraseñas no coinciden"),
});

type FormMail = yup.InferType<typeof schemaMail>;
type FormCodigo = yup.InferType<typeof schemaCodigo>;
type FormContrasenia = yup.InferType<typeof schemaContrasenia>;

const TEXT = {
    title: {
        ...TYPOGRAPHY_STYLES.rubik,
        fontSize: 24,
        fontWeight: 700,
    },
    subtitle: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        fontWeight: 500,
        opacity: 0.9,
    },
} as const;

function getErrorMessage(err: any, fallback: string) {
    const data = err?.response?.data ?? err?.data;

    return (
        data?.descripcionError ??
        data?.DescripcionError ??
        data?.mensaje ??
        data?.Mensaje ??
        (err?.message && err.message !== "Request failed with status code 400" ? err.message : null) ??
        fallback
    );
}

export default function RecuperoContrasenia() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const couponFieldStyle = getCouponFieldStyle(theme as any);

    const mailInicial = useMemo(() => searchParams.get("mail")?.trim() ?? "", [searchParams]);

    const [paso, setPaso] = useState<1 | 2 | 3>(1);
    const [mailConfirmado, setMailConfirmado] = useState(mailInicial);

    const [loadingEnviarCodigo, setLoadingEnviarCodigo] = useState(false);
    const [loadingValidarCodigo, setLoadingValidarCodigo] = useState(false);
    const [loadingRestablecer, setLoadingRestablecer] = useState(false);

    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();

    const {
        control: controlMail,
        handleSubmit: handleSubmitMail,
        reset: resetMail,
    } = useForm<FormMail>({
        resolver: yupResolver(schemaMail),
        defaultValues: { mail: mailInicial },
    });

    const {
        control: controlCodigo,
        handleSubmit: handleSubmitCodigo,
        reset: resetCodigo,
        getValues: getValuesCodigo,
    } = useForm<FormCodigo>({
        resolver: yupResolver(schemaCodigo),
        defaultValues: { codigo: "" },
    });

    const {
        control: controlContrasenia,
        handleSubmit: handleSubmitContrasenia,
        reset: resetContrasenia,
    } = useForm<FormContrasenia>({
        resolver: yupResolver(schemaContrasenia),
        defaultValues: {
            password: "",
            passwordConfirm: "",
        },
    });

    const limpiarMensajes = () => {
        setError(undefined);
        setSuccess(undefined);
    };

    const enviarCodigo = handleSubmitMail(async (data) => {
        limpiarMensajes();
        setLoadingEnviarCodigo(true);

        try {
            const response: RecuperoContraseniaResponse =
                await HttpAuth.enviarCodigoRecuperoContrasenia(data.mail);

            if (response.tieneError) {
                setError(response.descripcionError);
                return;
            }

            setMailConfirmado(data.mail.trim());
            setPaso(2);
            setSuccess(response.mensaje || "Te enviamos un código al mail indicado.");
        } catch (err: any) {
            setError(getErrorMessage(err, "No se pudo enviar el código."));
        } finally {
            setLoadingEnviarCodigo(false);
        }
    });

    const validarCodigo = handleSubmitCodigo(async (data) => {
        limpiarMensajes();
        setLoadingValidarCodigo(true);

        try {
            const response: RecuperoContraseniaResponse =
                await HttpAuth.verificarCodigoRecuperoContrasenia(mailConfirmado, data.codigo);

            if (response.tieneError) {
                setError(response.descripcionError);
                return;
            }

            setPaso(3);
            setSuccess(response.mensaje || "Código validado correctamente.");
        } catch (err: any) {
            setError(getErrorMessage(err, "El código es inválido o venció."));
        } finally {
            setLoadingValidarCodigo(false);
        }
    });

    const restablecerContrasenia = handleSubmitContrasenia(async (data) => {
        limpiarMensajes();
        setLoadingRestablecer(true);

        try {
            const codigoActual = getValuesCodigo("codigo");

            const response: RecuperoContraseniaResponse =
                await HttpAuth.restablecerContrasenia(
                    mailConfirmado,
                    codigoActual,
                    data.password
                );

            if (response.tieneError) {
                setError(response.descripcionError);
                return;
            }

            navigate(
                `/login?mail=${encodeURIComponent(mailConfirmado)}&restablecida=1`,
                { replace: true }
            );
        } catch (err: any) {
            setError(getErrorMessage(err, "No se pudo restablecer la contraseña."));
        } finally {
            setLoadingRestablecer(false);
        }
    });

    const volverACambiarMail = () => {
        limpiarMensajes();
        setPaso(1);
        resetCodigo({ codigo: "" });
        resetContrasenia({ password: "", passwordConfirm: "" });
        resetMail({ mail: mailConfirmado });
    };

    return (
        <Box sx={{ maxWidth: 520, mx: "auto", mt: 4, mb: 10 }}>
            <Paper
                sx={{
                    p: 3,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                }}
            >
                <Typography sx={{ ...TEXT.title, mb: 1 }}>
                    Recuperar contraseña
                </Typography>

                <Typography sx={{ ...TEXT.subtitle, mb: 3 }}>
                    Ingresá tu email, validá el código y elegí una nueva contraseña.
                </Typography>

                <Stack spacing={2.5}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ xs: "stretch", sm: "flex-start" }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <ControlledTextField
                                control={controlMail}
                                name="mail"
                                label="Email"
                                disabled={paso > 1 || loadingEnviarCodigo || loadingValidarCodigo || loadingRestablecer}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            onClick={enviarCodigo}
                            disabled={paso > 1 || loadingEnviarCodigo || loadingValidarCodigo || loadingRestablecer}
                            sx={{
                                ...BUTTON_STYLES.invertedPrimary,
                                minWidth: { xs: "100%", sm: 190 },
                                height: 56,
                            }}
                        >
                            {loadingEnviarCodigo ? "Enviando..." : "Enviar código al mail"}
                        </Button>
                    </Stack>

                    {paso > 1 && (
                        <Button
                            variant="text"
                            onClick={volverACambiarMail}
                            disabled={loadingEnviarCodigo || loadingValidarCodigo || loadingRestablecer}
                            sx={{
                                width: "fit-content",
                                minWidth: "auto",
                                p: 0,
                                mt: -1,
                                textTransform: "none",
                            }}
                        >
                            Cambiar email
                        </Button>
                    )}

                    <Collapse in={paso >= 2} unmountOnExit>
                        <Stack spacing={2.5}>
                            <ControlledTextField
                                control={controlCodigo}
                                name="codigo"
                                label="Código numérico"
                                disabled={paso > 2 || loadingValidarCodigo || loadingRestablecer}
                            />

                            <Button
                                variant="contained"
                                onClick={validarCodigo}
                                disabled={paso > 2 || loadingValidarCodigo || loadingRestablecer}
                                sx={{ ...BUTTON_STYLES.invertedPrimary, width: "100%" }}
                            >
                                {loadingValidarCodigo ? "Validando..." : "Validar código"}
                            </Button>
                        </Stack>
                    </Collapse>

                    <Collapse in={paso >= 3} unmountOnExit>
                        <Stack spacing={2.5}>
                            <ControlledTextPasswordFieldFilled
                                control={controlContrasenia}
                                name="password"
                                label="Nueva contraseña"
                                sx={couponFieldStyle}
                                disabled={loadingRestablecer}
                            />

                            <ControlledTextPasswordFieldFilled
                                control={controlContrasenia}
                                name="passwordConfirm"
                                label="Repetir nueva contraseña"
                                sx={couponFieldStyle}
                                disabled={loadingRestablecer}
                            />

                            <Button
                                variant="contained"
                                onClick={restablecerContrasenia}
                                disabled={loadingRestablecer}
                                sx={{ ...BUTTON_STYLES.invertedPrimary, width: "100%" }}
                            >
                                {loadingRestablecer ? "Restableciendo..." : "Restablecer contraseña"}
                            </Button>
                        </Stack>
                    </Collapse>

                    <Button
                        variant="outlined"
                        onClick={() => navigate(`/login${mailConfirmado ? `?mail=${encodeURIComponent(mailConfirmado)}` : ""}`)}
                        sx={{
                            ...(BUTTON_STYLES as any).outlinedPrimary,
                            width: "100%",
                        }}
                    >
                        Volver al login
                    </Button>

                    {success && <Alert severity="success">{success}</Alert>}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </Paper>
        </Box>
    );
}